import type { ParsedStreamData } from '@sweetistics/contracts/chat-stream';
import type { Dispatch, RefObject, SetStateAction } from 'react';
import { useCallback, useRef } from 'react';
import type { StreamingLogger } from '@/components/chat/chat-helpers';
import type { Message } from '@/components/chat/chat-types';
import { hasUsageMetrics, mergeUsageMetrics, parseUsageMetrics, type UsageMetrics } from '@/lib/ai/usage-metrics';
import { scheduleMicrotask } from '@/lib/react/hooks';
import { isPlainObject } from '@/lib/utils/type-guards';
import { parseStreamDataString } from '@/lib/validation/api/chat-stream-helpers';
import { appendAssistantDelta } from './helpers';
import type { ProcessStreamLine, StreamingState, StreamingUIEvent } from './types';
import type { UseStreamingErrorsReturn } from './useStreamingErrors';
import { useStreamingTools } from './useStreamingTools';

interface UseStreamingTokensOptions {
  errorHandlers: Pick<UseStreamingErrorsReturn, 'handleStreamError'>;
  log: StreamingLogger;
  onUiEvent?: (event: StreamingUIEvent) => void;
  onUsage?: (assistantId: string, usage: UsageMetrics) => void;
  setMessages: Dispatch<SetStateAction<Message[]>>;
  streamingStateRef: RefObject<StreamingState>;
  updateStreamingState: (state: StreamingState) => void;
}
const lookupPath = (value: unknown, path: readonly string[]): unknown => {
  let current: unknown = value;
  for (const segment of path) {
    if (!isPlainObject(current)) {
      return;
    }
    current = current[segment];
  }
  return current;
};

const extractUsageCandidates = (value: unknown): UsageMetrics[] => {
  if (value === undefined) {
    return [];
  }
  if (Array.isArray(value)) {
    return value.flatMap((entry) => extractUsageCandidates(entry));
  }
  const base = parseUsageMetrics(value);
  return base ? [base] : [];
};

const parseUsageUpdate = (raw: string): UsageMetrics | null => {
  try {
    const parsed: unknown = JSON.parse(raw);
    let candidates: UsageMetrics[] = extractUsageCandidates(parsed);
    const paths: string[][] = [
      ['usage'],
      ['data', 'usage'],
      ['metadata', 'usage'],
      ['metadata', 'delta', 'usage'],
      ['response', 'usage'],
      ['response', 'metadata', 'usage'],
      ['delta', 'usage'],
      ['message', 'usage'],
    ];
    for (const path of paths) {
      const nested = extractUsageCandidates(lookupPath(parsed, path));
      if (nested.length > 0) {
        candidates = [...candidates, ...nested];
      }
    }
    if (candidates.length === 0) {
      return null;
    }
    let merged: UsageMetrics = {};
    for (const entry of candidates) {
      merged = mergeUsageMetrics(merged, entry);
    }
    return merged;
  } catch {
    return null;
  }
};

const STREAM_PREFIX_PATTERN = /^data:\s*/;
const STREAM_INDEX_PATTERN = /^\d+:/;

export const useStreamingTokens = ({
  log,
  setMessages,
  streamingStateRef,
  updateStreamingState,
  errorHandlers,
  onUsage,
  onUiEvent,
}: UseStreamingTokensOptions) => {
  const debugLineCountReference = useRef(0);
  const { handleToolEvent } = useStreamingTools({
    log,
    setMessages,
    ...(typeof onUiEvent === 'function' ? { onUiEvent } : {}),
  });
  const pendingAssistantTextReference = useRef(new Map<string, string>());

  const appendAssistantText = useCallback(
    (assistantId: string, delta: string) => {
      if (!pendingAssistantTextReference.current.has(assistantId)) {
        pendingAssistantTextReference.current.set(assistantId, delta);
        scheduleMicrotask(() => {
          const pending = pendingAssistantTextReference.current.get(assistantId);
          if (!pending) {
            return;
          }
          pendingAssistantTextReference.current.delete(assistantId);
          setMessages((previous) => appendAssistantDelta(previous, assistantId, pending));
        });
        return;
      }
      const existing = pendingAssistantTextReference.current.get(assistantId) ?? '';
      pendingAssistantTextReference.current.set(assistantId, `${existing}${delta}`);
    },
    [setMessages]
  );

  const markStreamingStarted = useCallback(
    (assistantId: string) => {
      const current = streamingStateRef.current;
      if (current.started && current.assistantId === assistantId) {
        return;
      }
      updateStreamingState({ assistantId, started: true });
    },
    [streamingStateRef, updateStreamingState]
  );

  const handleParsedEvent = useCallback(
    (assistantId: string, parsed: ParsedStreamData) => {
      switch (parsed.type) {
        case 'text-delta': {
          markStreamingStarted(assistantId);
          appendAssistantText(assistantId, parsed.textDelta);
          onUiEvent?.({ type: 'text-delta', assistantId, deltaLength: parsed.textDelta.length });
          return;
        }
        case 'error': {
          const message = parsed.error || 'Chat stream error';
          errorHandlers.handleStreamError(message);
          throw new Error(message);
        }
        case 'tool-call':
        case 'tool-result':
        case 'tool-input-start':
        case 'tool-input-available':
        case 'tool-input-error':
        case 'tool-input-delta':
        case 'tool-output-available':
        case 'tool-output-error': {
          const handled = handleToolEvent(assistantId, parsed);
          if (!handled) {
            log.warn('Parsed event: unexpected tool payload received', { payload: parsed });
          }
          return;
        }
        default: {
          const exhaustiveCheck: never = parsed;
          log.warn('Parsed event: unexpected payload received', { payload: exhaustiveCheck });
          return;
        }
      }
    },
    [appendAssistantText, errorHandlers, handleToolEvent, log, markStreamingStarted, onUiEvent]
  );

  const processStreamLine = useCallback<ProcessStreamLine>(
    (line, assistantId) => {
      const raw = line.replace(STREAM_PREFIX_PATTERN, '').trim();
      if (debugLineCountReference.current < 5) {
        debugLineCountReference.current += 1;
        log.info('Stream line', { rawPreview: raw.slice(0, 160) });
      }
      if (!raw || raw === ':' || raw === '[DONE]') {
        return;
      }

      if (STREAM_INDEX_PATTERN.test(raw)) {
        const index = raw.indexOf(':');
        const streamIndex = raw.slice(0, index);
        const content = raw.slice(index + 1).trim();
        const usageUpdate = parseUsageUpdate(content);
        if (usageUpdate && onUsage && hasUsageMetrics(usageUpdate)) {
          onUsage(assistantId, usageUpdate);
        }
        if (streamIndex !== '0') {
          return;
        }
        const parsed = parseStreamDataString(content);
        if (parsed) {
          handleParsedEvent(assistantId, parsed);
        }
        return;
      }

      const usageUpdate = parseUsageUpdate(raw);
      if (usageUpdate && onUsage && hasUsageMetrics(usageUpdate)) {
        onUsage(assistantId, usageUpdate);
      }

      const maybe = parseStreamDataString(raw);
      if (!maybe) {
        return;
      }
      handleParsedEvent(assistantId, maybe);
    },
    [handleParsedEvent, log, onUsage]
  );

  const resetPendingAssistantText = useCallback((assistantId?: string) => {
    if (assistantId) {
      pendingAssistantTextReference.current.delete(assistantId);
      return;
    }
    pendingAssistantTextReference.current.clear();
  }, []);

  return { processStreamLine, resetPendingAssistantText };
};
